import { computed, ref, type Ref } from 'vue'
import type { ExternalToolsUI, McpServer, McpTool, McpToolAnnotations } from 'shared/src'
import fetchApi from '@/utils/fetchApi'
import { useToast } from '@ai/ui-components/toast'
import { LIST_ACTION_ICONS } from '@/components/BusinessLogic/TableActions'
import { isNotBlank } from '@/utils/String'

export interface McpDiscoveredTool {
  name: string
  description?: string
  inputSchema?: Record<string, unknown>
  annotations?: McpToolAnnotations
}

export interface McpTestResult {
  status: 'ok' | 'error'
  tools?: McpDiscoveredTool[]
  error_code?: string
}

export const categoryFromAnnotations = (annotations?: McpToolAnnotations): 'read' | 'write' => {
  if (annotations?.destructiveHint === true) return 'write'
  if (annotations?.readOnlyHint === false) return 'write'
  return 'read'
}

export const mergeDiscoveredTools = (
  existing: McpTool[],
  discovered: McpDiscoveredTool[],
): McpTool[] =>
  discovered.map((d) => {
    const prev = existing.find((t) => t.name === d.name)
    return {
      name: d.name,
      description: d.description,
      inputSchema: d.inputSchema,
      annotations: d.annotations,
      enabled: prev?.enabled ?? true,
      description_override: prev?.description_override,
      category: prev?.category ?? categoryFromAnnotations(d.annotations),
    }
  })

export const formatToolTooltip = (
  tool: McpTool,
  t: (key: string, payload?: Record<string, unknown>) => string,
): string => {
  let text = isNotBlank(tool.description_override)
    ? tool.description_override
    : isNotBlank(tool.description)
      ? tool.description
      : tool.name
  const schema = tool.inputSchema
  if (schema && typeof schema === 'object' && schema.properties) {
    const props = schema.properties as Record<string, { type?: string }>
    const required = Array.isArray(schema.required) ? (schema.required as string[]) : []
    const params = Object.entries(props).map(([name, prop]) => {
      const type = prop.type ?? 'any'
      const req = required.includes(name) ? ` (${t('tools.mcp.required')})` : ''
      return `• ${name}: ${type}${req}`
    })
    if (params.length) {
      text += `\n\n${t('tools.mcp.parameters')}:\n${params.join('\n')}`
    }
  }
  return text
}

interface McpModalRef {
  openPanel: (server?: McpServer) => void
}

const MAX_SERVERS = 10

export const useMcpServers = (
  externalTools: Ref<ExternalToolsUI>,
  mcpModalRef: Ref<McpModalRef | undefined>,
) => {
  const { t } = useI18n()
  const toast = useToast()

  const canAddMcpServer = computed(() => externalTools.value.servers.length < MAX_SERVERS)
  const mcpEditingIndex = ref<number | null>(null)
  const mcpDeleteIndex = ref<number | null>(null)
  const mcpDeleteOpen = ref(false)

  const openAddMcpModal = () => {
    mcpEditingIndex.value = null
    mcpModalRef.value?.openPanel()
  }

  const openEditMcpModal = (index: number) => {
    mcpEditingIndex.value = index
    mcpModalRef.value?.openPanel(externalTools.value.servers[index])
  }

  const handleMcpConfirm = (server: McpServer) => {
    if (mcpEditingIndex.value !== null) {
      externalTools.value.servers[mcpEditingIndex.value] = server
    } else {
      externalTools.value.servers.push(server)
    }
  }

  const openDeleteMcpModal = (index: number) => {
    mcpDeleteIndex.value = index
    mcpDeleteOpen.value = true
  }

  const confirmDeleteMcpServer = (): boolean => {
    if (mcpDeleteIndex.value !== null) {
      externalTools.value.servers.splice(mcpDeleteIndex.value, 1)
    }
    mcpDeleteOpen.value = false
    return true
  }

  const testMcpConnection = async (index: number) => {
    const server = externalTools.value.servers[index]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!server) return

    try {
      const response = await fetchApi<McpTestResult>(
        {
          endpoint: '/api/configurations/mcp-test',
          method: 'POST',
          body: { url: server.url, headers: server.headers },
        },
        { suppressAlert: true },
      )

      if (response.status === 'ok') {
        const discovered = response.tools ?? []
        externalTools.value.servers[index] = {
          ...server,
          tools: mergeDiscoveredTools(server.tools, discovered),
        }
        const message =
          discovered.length > 0
            ? `${t('tools.mcp.testSuccess')} — ${t('tools.mcp.toolsDiscovered', { count: discovered.length })}`
            : t('tools.mcp.testSuccess')
        toast.success(message)
      } else {
        const errorKey = response.error_code
          ? `tools.mcp.errors.${response.error_code}`
          : 'tools.mcp.testError'
        toast.error(t(errorKey), { title: t('error_occurred') })
      }
    } catch {
      toast.error(t('tools.mcp.testError'), { title: t('error_occurred') })
    }
  }

  const getMcpActions = (index: number) => [
    {
      label: t('tools.mcp.edit'),
      value: 'edit',
      icon: LIST_ACTION_ICONS.edit,
      handler: () => {
        openEditMcpModal(index)
      },
      dataTestid: `mcp-edit-${String(index)}`,
    },
    {
      label: t('tools.mcp.testConnection'),
      value: 'test',
      icon: LIST_ACTION_ICONS.liveTest,
      handler: () => testMcpConnection(index),
      dataTestid: `mcp-test-${String(index)}`,
    },
    {
      label: t('tools.mcp.delete'),
      value: 'delete',
      icon: LIST_ACTION_ICONS.deleteDanger,
      handler: () => {
        openDeleteMcpModal(index)
      },
      danger: true,
      dataTestid: `mcp-delete-${String(index)}`,
    },
  ]

  return {
    canAddMcpServer,
    mcpEditingIndex,
    mcpDeleteIndex,
    mcpDeleteOpen,
    openAddMcpModal,
    openEditMcpModal,
    handleMcpConfirm,
    openDeleteMcpModal,
    confirmDeleteMcpServer,
    testMcpConnection,
    getMcpActions,
    formatToolTooltip: (tool: McpTool) => formatToolTooltip(tool, t),
  }
}
